import React, { ChangeEvent, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { BonusCampaignCountryDto } from 'api/Wallet/Types/MarketingBonusCampaignDto'
import { MultiSelect } from 'components/Select/Select'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { useAppSelector } from 'store'

import { TickmillCompanyDto } from '../../../api/Identity/Types/Company'
import {
  FormBaseButtonContainer,
  FormBaseContainer,
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
} from '../../../components/Form/Form.styles'

export interface EditBonusCountryFormValues {
  bonusCampaignCountryOptionId: string
  tickmillCompanyId: number
  minimumAmount: number
  isActive: boolean
  countryIds: string[]
}

const EditBonusCountryFormUI: React.FC<FormikProps<EditBonusCountryFormValues> & OuterProps> = (
  props
) => {
  const { handleSubmit, setFieldValue, setSubmitting, isSubmitting, values, errors } = props
  const countriesList = useAppSelector((state) => state.identity.countries)

  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  const existingCountryOptionsIds = useMemo(() => {
    if (!props.existingCountryOptions || values.tickmillCompanyId === 0) {
      return new Set<string>()
    }

    return new Set(
      props.existingCountryOptions
        .filter(
          (x) =>
            x.tickmillCompany.id === values.tickmillCompanyId &&
            x.bonusCampaignCountryOptionId !== values.bonusCampaignCountryOptionId
        )
        .flatMap((x) => x.countries.map((c) => c.id))
    )
  }, [props.existingCountryOptions, values.tickmillCompanyId, values.bonusCampaignCountryOptionId])

  const availableCountries = useMemo(() => {
    return countriesList.filter(
      (country) =>
        !existingCountryOptionsIds.has(country.id) || values.countryIds.includes(country.id)
    )
  }, [countriesList, existingCountryOptionsIds, values.countryIds])

  return (
    <FormBaseContainer>
      <Form>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Minimum Amount</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field
                type='number'
                name='minimumAmount'
                value={values.minimumAmount}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('minimumAmount', Number(event.target.value))
                }
              />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.minimumAmount && (
            <FormBaseErrorMessage>{errors.minimumAmount}</FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Tickmill Company</FormBaseFieldLabel>
            <Field
              component='select'
              name='tickmillCompanyId'
              value={values.tickmillCompanyId}
              onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                setFieldValue('tickmillCompanyId', Number(event.target.value))
                setFieldValue('countryIds', [])
              }}
            >
              <option value=''>Select a company</option>
              {props.tickmillCompanies.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </Field>
          </FormBaseRow>
          {errors.tickmillCompanyId && (
            <FormBaseErrorMessage>{errors.tickmillCompanyId}</FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Countries</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <MultiSelect
                name='countryIds'
                placeholder='Countries'
                onChange={(selectedIds: string[]) => {
                  setFieldValue('countryIds', selectedIds)
                }}
                fullWidth
                value={values.countryIds}
              >
                {availableCountries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </MultiSelect>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.countryIds && <FormBaseErrorMessage>{errors.countryIds}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Enabled</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field
                name='isActive'
                type='checkbox'
                checked={values.isActive}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('isActive', event.target.checked)
                }
              />
            </FormBaseFieldGroup>
          </FormBaseRow>
        </FormBaseFieldBlock>
        <FormBaseButtonContainer>
          <Button disabled={isSubmitting} key='button' type='button' onClick={handleSubmitForm}>
            Submit
          </Button>
        </FormBaseButtonContainer>
      </Form>
    </FormBaseContainer>
  )
}

function dataToSubmit(values: EditBonusCountryFormValues): EditBonusCountryFormValues {
  return {
    bonusCampaignCountryOptionId: values.bonusCampaignCountryOptionId,
    tickmillCompanyId: values.tickmillCompanyId,
    minimumAmount: values.minimumAmount,
    isActive: values.isActive,
    countryIds: values.countryIds,
  }
}

interface OuterProps {
  tickmillCompanies: TickmillCompanyDto[]
  existingCountryOptions?: BonusCampaignCountryDto[]
  initialValues: EditBonusCountryFormValues

  onSubmit(values: EditBonusCountryFormValues): Promise<void>
}

export const EditBonusCampaignCountryForm = withFormik<OuterProps, EditBonusCountryFormValues>({
  mapPropsToValues: ({ initialValues }) => {
    return {
      bonusCampaignCountryOptionId: initialValues.bonusCampaignCountryOptionId,
      tickmillCompanyId: initialValues.tickmillCompanyId,
      minimumAmount: initialValues.minimumAmount,
      isActive: initialValues.isActive,
      countryIds: initialValues.countryIds,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      const data = dataToSubmit(values)
      await props.onSubmit(data)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<EditBonusCountryFormValues> = {}
    if (!values.tickmillCompanyId) {
      errors.tickmillCompanyId = 'Tickmill Company is required'
    }
    if (!values.countryIds || values.countryIds.length === 0) {
      errors.countryIds = 'Countries are required'
    }
    return errors
  },
  enableReinitialize: true,
})(EditBonusCountryFormUI)
